/**
 * The names an installation and core agree on in the status and the report (ADR 0016). The
 * schemas take any short string where these go, so an older core still accepts a name a newer
 * installation adds: the lists say what an installation sends, not what core may receive.
 */

/** Lower edges in days of the recency buckets: within 7, 30, 90, 180 or 365 days, then over a year. */
export const RECENCY_DAYS = [7, 30, 90, 180, 365] as const;

/** Requests are counted per route group: never per slug, id or query. */
export const ROUTE_GROUPS = [
  "garage_trpc",
  "trpc",
  "admin",
  "account",
  "files",
  "static",
  "internal",
  "other",
] as const;
export type RouteGroup = (typeof ROUTE_GROUPS)[number];

/** Lower edges of the latency buckets in milliseconds; "over 2.5 s" starts at 2,500. */
export const LATENCY_EDGES_MS = [0, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000] as const;

/**
 * Counters of misuse and mail in the status, each with a value:
 *
 * - `signin.failed`, `password_reset`: no value;
 * - `rate_limit`: the limit's kind;
 * - `invitation.sent`, `upload.bytes` (the count is bytes): the garage's id;
 * - `http.401`, `http.403`: the route group;
 * - `upload.refused`: the reason;
 * - `mail.sent`, `mail.failed`: the mail's kind.
 *
 * Counts only, never who or from where.
 */
export const SIGNALS = [
  "signin.failed",
  "rate_limit",
  "password_reset",
  "invitation.sent",
  "http.401",
  "http.403",
  "upload.refused",
  "upload.bytes",
  "mail.sent",
  "mail.failed",
] as const;
export type Signal = (typeof SIGNALS)[number];

export const MAIL_KINDS = ["invitation", "password_reset", "demo_link"] as const;
export type MailKind = (typeof MAIL_KINDS)[number];

/**
 * Day counts an installation keeps (`usage_counts`) and reports for the last 35 days. Each has a
 * value: `device.*` the class, `locale.region` the region, `demo.started` "example" or "empty" and
 * the language ("example:de"); the visit metrics are in `visits.ts`. The others have none.
 */
export const USAGE_METRICS = [
  "users.active",
  "garages.active",
  "device.type",
  "device.os",
  "device.browser",
  "locale.region",
  "signins",
  "signouts",
  "garages.deleted",
  "users.deleted",
  "demo.requested",
  "demo.opened",
  "demo.started",
  "demo.ended",
  "demo.expired",
  "demo.busy",
] as const;
export type UsageMetric = (typeof USAGE_METRICS)[number];

/** The ten features a garage can use; how many it uses is its feature breadth. */
export const FEATURES = [
  "vehicles",
  "work_orders",
  "templates",
  "orders",
  "sources",
  "alternatives",
  "locations",
  "tags",
  "attachments",
  "chassis_codes",
] as const;
export type Feature = (typeof FEATURES)[number];

/**
 * Flags in the garage table: where a garage stands from created to gone quiet, whether it is
 * left out of statistics, and `outlier:<measure>` per measure it is an outlier in.
 */
export const GARAGE_FLAGS = [
  "unclaimed",
  "never_opened",
  "stalled",
  "dormant",
  "stats_excluded",
] as const;
export type GarageFlag = (typeof GARAGE_FLAGS)[number];

/** How a demo ended: by the visitor, after its time, its link unopened, by a new one, half-made. */
export const DEMO_END_REASONS = [
  "visitor",
  "expired",
  "link_expired",
  "replaced",
  "failed",
] as const;
export type DemoEndReason = (typeof DEMO_END_REASONS)[number];
